/**
 * Tenant Badge Components
 *
 * Badge rendering functions for tenant status, isolation, and health.
 * Returns JSX elements with consistent styling.
 *
 * @module features/tenants/utils/badges
 */

import {
  CheckCircleIcon,
  AlertIcon,
  RefreshIcon,
  ArchiveIcon,
} from '../../../components/common/Icons';
import type {
  TenantStatus,
  IsolationLevel,
  Tenant,
  ContainerStatus,
  ContainerStatusValue,
  ContainerTransition,
} from '../types';

// ============================================================================
// Types
// ============================================================================

/**
 * Health status result with display properties.
 */
export interface HealthStatusResult {
  label: string;
  color: 'success' | 'warning' | 'error' | 'info' | 'secondary';
  clickable: boolean;
}

/**
 * Container statuses map by tenant ID.
 */
export type ContainerStatusMap = Record<string, ContainerStatus>;

/**
 * Container transition states by tenant ID.
 */
export type ContainerTransitionMap = Record<string, ContainerTransition>;

// ============================================================================
// Tenant Status Badge
// ============================================================================

/**
 * Get badge JSX for tenant status.
 *
 * @param status - Tenant status value
 * @returns JSX element for the status badge
 */
export const getStatusBadge = (status: TenantStatus | string): JSX.Element => {
  switch (status) {
    case 'active':
      return (
        <span className="badge badge-success" style={{ whiteSpace: 'nowrap' }}>
          <CheckCircleIcon size={14} /> Active
        </span>
      );
    case 'pending':
      return (
        <span className="badge badge-warning" style={{ whiteSpace: 'nowrap' }}>
          <AlertIcon size={14} /> Pending
        </span>
      );
    case 'suspended':
      return (
        <span className="badge badge-warning" style={{ whiteSpace: 'nowrap' }}>
          <AlertIcon size={14} /> Suspended
        </span>
      );
    case 'provisioning':
      return (
        <span
          className="badge badge-info"
          style={{
            whiteSpace: 'nowrap',
            animation: 'pulse-soft 2s ease-in-out infinite',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <RefreshIcon size={14} className="spin" />
          Provisioning
        </span>
      );
    case 'upgrading':
      return (
        <span
          className="badge badge-warning"
          style={{
            whiteSpace: 'nowrap',
            animation: 'pulse-soft 2s ease-in-out infinite',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <RefreshIcon size={14} className="spin" />
          Upgrading
        </span>
      );
    case 'archived':
      return (
        <span className="badge badge-secondary" style={{ whiteSpace: 'nowrap' }}>
          <ArchiveIcon size={14} /> Archived
        </span>
      );
    default:
      return (
        <span className="badge badge-secondary" style={{ whiteSpace: 'nowrap' }}>
          {status}
        </span>
      );
  }
};

// ============================================================================
// Isolation Level Badge
// ============================================================================

/**
 * Get badge JSX for isolation level.
 *
 * @param level - Isolation level value
 * @returns JSX element for the isolation badge
 */
export const getIsolationBadge = (level: IsolationLevel | string): JSX.Element => {
  switch (level) {
    case 'row':
      return <span className="badge badge-info">Row-Level</span>;
    case 'schema':
      return <span className="badge badge-primary">Schema</span>;
    case 'container':
      return <span className="badge badge-accent">Container</span>;
    default:
      return <span className="badge badge-secondary">{level}</span>;
  }
};

// ============================================================================
// Container Status Badge
// ============================================================================

/**
 * Get badge JSX for container status.
 *
 * @param status - Container status value
 * @returns JSX element for the container status badge
 */
export const getContainerStatusBadge = (status: ContainerStatusValue | string): JSX.Element => {
  switch (status) {
    case 'running':
      return (
        <span className="badge badge-success" style={{ whiteSpace: 'nowrap' }}>
          <CheckCircleIcon size={14} /> Healthy
        </span>
      );
    case 'stopped':
      return (
        <span className="badge badge-secondary" style={{ whiteSpace: 'nowrap' }}>
          Stopped
        </span>
      );
    case 'unhealthy':
      return (
        <span className="badge badge-warning" style={{ whiteSpace: 'nowrap' }}>
          <AlertIcon size={14} /> Unhealthy
        </span>
      );
    case 'error':
      return (
        <span className="badge badge-error" style={{ whiteSpace: 'nowrap' }}>
          <AlertIcon size={14} /> Error
        </span>
      );
    case 'provisioning':
      return (
        <span className="badge badge-info" style={{ whiteSpace: 'nowrap' }}>
          <RefreshIcon size={14} className="spin" /> Provisioning
        </span>
      );
    case 'starting':
      return (
        <span className="badge badge-info" style={{ whiteSpace: 'nowrap' }}>
          <RefreshIcon size={14} className="spin" /> Starting
        </span>
      );
    case 'not_provisioned':
      return (
        <span className="badge badge-warning" style={{ whiteSpace: 'nowrap' }}>
          Not Provisioned
        </span>
      );
    default:
      return (
        <span className="badge badge-secondary" style={{ whiteSpace: 'nowrap' }}>
          {status}
        </span>
      );
  }
};

// ============================================================================
// Health Status
// ============================================================================

/**
 * Determine health status based on tenant type and container status.
 *
 * @param tenant - The tenant to check
 * @param containerStatuses - Map of container statuses by tenant ID
 * @param containerTransitioning - Map of container transitions by tenant ID
 * @returns Health status with label, color, and clickable flag
 */
export const getHealthStatus = (
  tenant: Tenant,
  containerStatuses: ContainerStatusMap = {},
  containerTransitioning: ContainerTransitionMap = {}
): HealthStatusResult => {
  switch (tenant.isolation_level) {
    case 'row':
      return { label: 'Healthy', color: 'success', clickable: false };

    case 'schema':
      if (tenant.schema_name) {
        return { label: 'Healthy', color: 'success', clickable: true };
      }
      return { label: 'Not Provisioned', color: 'warning', clickable: true };

    case 'container': {
      // Pending container tenants haven't been provisioned yet
      if (tenant.status === 'pending') {
        return { label: 'Not Provisioned', color: 'warning', clickable: true };
      }

      // Check if container action is in progress (start/stop/restart)
      const transitioning = containerTransitioning[tenant.id];
      if (transitioning) {
        if (transitioning.action === 'start') {
          return { label: 'Starting...', color: 'info', clickable: true };
        }
        if (transitioning.action === 'stop') {
          return { label: 'Stopping...', color: 'info', clickable: true };
        }
        if (transitioning.action === 'restart') {
          return { label: 'Restarting...', color: 'info', clickable: true };
        }
      }

      const containerStatus = containerStatuses[tenant.id];
      if (!containerStatus || containerStatus.status === 'not_provisioned') {
        // If tenant is active, containers exist but status hasn't loaded yet
        if (tenant.status === 'active') {
          return { label: 'Loading...', color: 'info', clickable: true };
        }
        return { label: 'Not Provisioned', color: 'warning', clickable: true };
      }
      if (containerStatus.status === 'provisioning') {
        return { label: 'Provisioning...', color: 'info', clickable: true };
      }
      if (containerStatus.status === 'starting') {
        return { label: 'Starting...', color: 'info', clickable: true };
      }
      if (containerStatus.status === 'running') {
        // Check if all containers healthy
        const containers = containerStatus.containers || {};
        const allHealthy = Object.values(containers).every(
          (c) => c.running && c.healthy
        );
        if (allHealthy) {
          return { label: 'Healthy', color: 'success', clickable: true };
        }
        return { label: 'Degraded', color: 'warning', clickable: true };
      }
      if (containerStatus.status === 'stopped') {
        return { label: 'Stopped', color: 'secondary', clickable: true };
      }
      if (containerStatus.status === 'error' || containerStatus.status === 'unhealthy') {
        return { label: 'Error', color: 'error', clickable: true };
      }
      return { label: 'Unknown', color: 'secondary', clickable: true };
    }

    default:
      return { label: '—', color: 'secondary', clickable: false };
  }
};

// ============================================================================
// Health Value (for filtering)
// ============================================================================

/**
 * Label to filter value mapping.
 */
const healthLabelToValue: Record<string, string> = {
  'Healthy': 'healthy',
  'Not Provisioned': 'not_provisioned',
  'Provisioning...': 'provisioning',
  'Starting...': 'starting',
  'Stopping...': 'stopping',
  'Restarting...': 'restarting',
  'Stopped': 'stopped',
  'Error': 'error',
  'Degraded': 'error',
  'Unknown': 'unknown',
};

/**
 * Get health value for filtering.
 *
 * @param tenant - The tenant to check
 * @param containerStatuses - Map of container statuses by tenant ID
 * @param containerTransitioning - Map of container transitions by tenant ID
 * @returns Normalized health value string for filtering
 */
export const getHealthValue = (
  tenant: Tenant,
  containerStatuses: ContainerStatusMap = {},
  containerTransitioning: ContainerTransitionMap = {}
): string => {
  const health = getHealthStatus(tenant, containerStatuses, containerTransitioning);
  return healthLabelToValue[health.label] || 'unknown';
};
